import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import { FC, useEffect, useState } from 'react';
import { Box, DialogContent, DialogContentText, TextField, Typography } from '@mui/material';
import { nanoid } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { addMtdCommentRequest, sendMtdFileReportRequest } from 'redux/reducers/hmrc/reducer';
import { TFileReportDialogProps } from './types';
import { validationSchema } from './validationSchema';
import { EasTableValue } from '../../../ui/EasTable/EasTableRow/EasTableValue';
import { ETableStructureType } from '../../../ui/EasTable/types';

export const FileReportDialog: FC<TFileReportDialogProps> = ({
	open,
	setClose,
	id,
	item,
	title = 'File Report',
	text = '',
	mtdReportsRequestData,
}) => {
	const dispatch = useDispatch();
	const [newComment, setNewComment] = useState<string>('');

	const reset = () => {
		setNewComment('');
	};

	const formik = useFormik({
		initialValues: {
			calculatedVatDueSales: 0,
			calculatedVatDueAcquisitions: 0,
			calculatedTotalVatDue: 0,
			calculatedVatReclaimedCurrPeriod: 0,
			calculatedNetVatDue: 0,
			calculatedTotalValueSalesExVAT: 0,
			calculatedTotalValuePurchasesExVAT: 0,
			calculatedTotalValueGoodsSuppliedExVAT: 0,
			calculatedTotalAcquisitionsExVAT: 0,
		},
		enableReinitialize: true,
		validationSchema,
		onSubmit: (values) => {
			if (id === null) return;
			console.log('onSubmit', values);
			dispatch(
				sendMtdFileReportRequest({
					id,
					data: {
						calculatedVatDueSales: Number(values.calculatedVatDueSales),
						calculatedVatDueAcquisitions: Number(values.calculatedVatDueAcquisitions),
						calculatedTotalVatDue: Number(
							Number(
								Math.abs(
									Number(values.calculatedVatDueSales) +
										Number(values.calculatedVatDueAcquisitions),
								),
							).toFixed(2),
						),
						calculatedVatReclaimedCurrPeriod: Number(values.calculatedVatReclaimedCurrPeriod),
						calculatedNetVatDue: Number(
							Math.abs(
								Number(values.calculatedVatDueSales) +
									Number(values.calculatedVatDueAcquisitions) -
									Number(values.calculatedVatReclaimedCurrPeriod),
							).toFixed(2),
						),
						calculatedTotalValueSalesExVAT: Number(values.calculatedTotalValueSalesExVAT),
						calculatedTotalValuePurchasesExVAT: Number(values.calculatedTotalValuePurchasesExVAT),
						calculatedTotalValueGoodsSuppliedExVAT: Number(
							values.calculatedTotalValueGoodsSuppliedExVAT,
						),
						calculatedTotalAcquisitionsExVAT: Number(values.calculatedTotalAcquisitionsExVAT),
					},
					getMtdReportsRequest: mtdReportsRequestData,
				}),
			);
			if (newComment) {
				dispatch(addMtdCommentRequest({ id, comment: newComment }));
			}
			formik.resetForm();
			reset();
			setClose();
		},
	});

	useEffect(() => {
		if (!open) return;
		if (!item) return;

		// Initialize form with calculated values from item
		formik
			.setValues({
				calculatedVatDueSales: item.calculatedVatDueSales || 0,
				calculatedVatDueAcquisitions: item.calculatedVatDueAcquisitions || 0,
				calculatedTotalVatDue:
					Number(item.calculatedVatDueSales) + Number(item.calculatedVatDueAcquisitions),
				calculatedVatReclaimedCurrPeriod: item.calculatedVatReclaimedCurrPeriod || 0,
				calculatedNetVatDue: Math.abs(
					Number(item.calculatedVatDueSales) +
						Number(item.calculatedVatDueAcquisitions) -
						Number(item.calculatedVatReclaimedCurrPeriod),
				),
				calculatedTotalValueSalesExVAT: item.calculatedTotalValueSalesExVAT || 0,
				calculatedTotalValuePurchasesExVAT: item.calculatedTotalValuePurchasesExVAT || 0,
				calculatedTotalValueGoodsSuppliedExVAT: item.calculatedTotalValueGoodsSuppliedExVAT || 0,
				calculatedTotalAcquisitionsExVAT: item.calculatedTotalAcquisitionsExVAT || 0,
			})
			.catch(() => {});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [item, open]);

	return (
		<Dialog
			open={open}
			onClose={setClose}
			sx={{
				'& .MuiDialog-paper': {
					width: '700px',
					maxWidth: '700px',
					borderRadius: '12px',
				},
			}}
		>
			<DialogTitle color="primary" dangerouslySetInnerHTML={{ __html: title }} />
			<form onSubmit={formik.handleSubmit}>
				<DialogContent
					sx={{
						p: '0 12px 0px 12px',
					}}
				>
					<Box sx={{ display: 'flex', flexDirection: 'row', gap: '32px', mb: '12px' }}>
						<Typography component="div" color="primary" sx={{ fontSize: '0.9rem' }}>
							Full Name: <b>{item?.emFullName}</b>
						</Typography>
						<Typography component="div" color="primary" sx={{ fontSize: '0.9rem' }}>
							Reporting Period:{' '}
							<b>
								<EasTableValue value={item?.reportPeriod} type={ETableStructureType.DATE_PERIOD} />
							</b>
						</Typography>
						<Typography component="div" color="primary" sx={{ fontSize: '0.9rem' }}>
							Due Date:{' '}
							<b>
								<EasTableValue value={item?.reportDueDate} type={ETableStructureType.DATE} />
							</b>
						</Typography>
					</Box>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							gap: '16px',
						}}
					>
						<Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
							<TextField
								id="calculatedVatDueSales"
								name="calculatedVatDueSales"
								size="small"
								sx={{
									mt: '12px',
								}}
								label="BOX 1 (Calculated VAT Due Sales)"
								value={formik.values.calculatedVatDueSales}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="number"
								fullWidth
								error={
									formik.touched.calculatedVatDueSales &&
									Boolean(formik.errors.calculatedVatDueSales)
								}
								helperText={
									formik.touched.calculatedVatDueSales && formik.errors.calculatedVatDueSales
								}
							/>

							<TextField
								id="calculatedVatDueAcquisitions"
								name="calculatedVatDueAcquisitions"
								size="small"
								sx={{
									mt: '12px',
								}}
								label="BOX 2 (Calculated VAT Due Acquisitions)"
								value={formik.values.calculatedVatDueAcquisitions}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="number"
								fullWidth
								error={
									formik.touched.calculatedVatDueAcquisitions &&
									Boolean(formik.errors.calculatedVatDueAcquisitions)
								}
								helperText={
									formik.touched.calculatedVatDueAcquisitions &&
									formik.errors.calculatedVatDueAcquisitions
								}
							/>

							<TextField
								id="calculatedTotalVatDue"
								name="calculatedTotalVatDue"
								size="small"
								sx={{
									mt: '12px',
								}}
								label="BOX 3 (Calculated Total VAT Due)"
								value={Number(
									Number(
										Number(formik.values.calculatedVatDueSales) +
											Number(formik.values.calculatedVatDueAcquisitions),
									).toFixed(2),
								)}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="number"
								fullWidth
								// error={
								// 	formik.touched.calculatedTotalVatDue &&
								// 	Boolean(formik.errors.calculatedTotalVatDue)
								// }
								// helperText={
								// 	formik.touched.calculatedTotalVatDue && formik.errors.calculatedTotalVatDue
								// }
								disabled
							/>

							<TextField
								id="calculatedVatReclaimedCurrPeriod"
								name="calculatedVatReclaimedCurrPeriod"
								size="small"
								sx={{
									mt: '12px',
								}}
								label="BOX 4 (Calculated VAT Reclaimed Current Period)"
								value={formik.values.calculatedVatReclaimedCurrPeriod}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="number"
								fullWidth
								error={
									formik.touched.calculatedVatReclaimedCurrPeriod &&
									Boolean(formik.errors.calculatedVatReclaimedCurrPeriod)
								}
								helperText={
									formik.touched.calculatedVatReclaimedCurrPeriod &&
									formik.errors.calculatedVatReclaimedCurrPeriod
								}
							/>

							<TextField
								id="calculatedNetVatDue"
								name="calculatedNetVatDue"
								size="small"
								sx={{
									mt: '12px',
								}}
								label="BOX 5 (Calculated Net VAT Due)"
								value={Number(
									Math.abs(
										Math.abs(
											Number(formik.values.calculatedVatDueSales) +
												Number(formik.values.calculatedVatDueAcquisitions) -
												Number(formik.values.calculatedVatReclaimedCurrPeriod),
										),
									).toFixed(2),
								)}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="number"
								fullWidth
								// error={
								// 	formik.touched.calculatedNetVatDue && Boolean(formik.errors.calculatedNetVatDue)
								// }
								// helperText={formik.touched.calculatedNetVatDue && formik.errors.calculatedNetVatDue}
								disabled
							/>
							<TextField
								id="calculatedTotalValueSalesExVAT"
								name="calculatedTotalValueSalesExVAT"
								size="small"
								sx={{
									mt: '12px',
								}}
								label="BOX 6 (Calculated Total Value Sales Ex VAT)"
								value={formik.values.calculatedTotalValueSalesExVAT}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="number"
								fullWidth
								error={
									formik.touched.calculatedTotalValueSalesExVAT &&
									Boolean(formik.errors.calculatedTotalValueSalesExVAT)
								}
								helperText={
									formik.touched.calculatedTotalValueSalesExVAT &&
									formik.errors.calculatedTotalValueSalesExVAT
								}
							/>

							<TextField
								id="calculatedTotalAcquisitionsExVAT"
								name="calculatedTotalAcquisitionsExVAT"
								size="small"
								sx={{
									mt: '12px',
								}}
								label="BOX 7 (Calculated Total Acquisitions Ex VAT)"
								value={formik.values.calculatedTotalAcquisitionsExVAT}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="number"
								fullWidth
								error={
									formik.touched.calculatedTotalAcquisitionsExVAT &&
									Boolean(formik.errors.calculatedTotalAcquisitionsExVAT)
								}
								helperText={
									formik.touched.calculatedTotalAcquisitionsExVAT &&
									formik.errors.calculatedTotalAcquisitionsExVAT
								}
							/>

							<TextField
								id="calculatedTotalValueGoodsSuppliedExVAT"
								name="calculatedTotalValueGoodsSuppliedExVAT"
								size="small"
								sx={{
									mt: '12px',
								}}
								label="BOX 8 (Calculated Total Value Goods Supplied Ex VAT)"
								value={formik.values.calculatedTotalValueGoodsSuppliedExVAT}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="number"
								fullWidth
								error={
									formik.touched.calculatedTotalValueGoodsSuppliedExVAT &&
									Boolean(formik.errors.calculatedTotalValueGoodsSuppliedExVAT)
								}
								helperText={
									formik.touched.calculatedTotalValueGoodsSuppliedExVAT &&
									formik.errors.calculatedTotalValueGoodsSuppliedExVAT
								}
							/>

							<TextField
								id="calculatedTotalValuePurchasesExVAT"
								name="calculatedTotalValuePurchasesExVAT"
								size="small"
								sx={{
									mt: '12px',
								}}
								label="BOX 9 (Calculated Total Value Purchases Ex VAT)"
								value={formik.values.calculatedTotalValuePurchasesExVAT}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								type="number"
								fullWidth
								error={
									formik.touched.calculatedTotalValuePurchasesExVAT &&
									Boolean(formik.errors.calculatedTotalValuePurchasesExVAT)
								}
								helperText={
									formik.touched.calculatedTotalValuePurchasesExVAT &&
									formik.errors.calculatedTotalValuePurchasesExVAT
								}
							/>
						</Box>

						<Box
							sx={{
								width: '100%',
								border: '1px solid #016193',
								p: '8px',
								borderRadius: '12px',
								display: 'flex',
								flexDirection: 'column',
								gap: '8px',
								justifyContent: 'space-between',
							}}
						>
							<Box>
								<Typography color="primary" sx={{ fontSize: '0.9rem' }}>
									Comments:
								</Typography>
								<Box
									sx={{
										fontSize: '0.8rem',
										display: 'flex',
										flexDirection: 'column',
										gap: '8px',
									}}
								>
									{!!item &&
										Array.isArray(item.comments) &&
										item.comments?.map((comment) => (
											<Box
												key={nanoid()}
												sx={{
													lineHeight: '1.2',
													backgroundColor: '#cadbe6',
													color: 'primary.main',
													// opacity: '0.8',
													padding: '6px 8px',
													borderRadius: '12px',
													// color: '#000000a0',
												}}
											>
												<Box
													sx={{
														fontSize: '0.8rem',
														display: 'flex',
														flexDirection: 'row',
														justifyContent: 'space-between',
														// gap: '8px',
													}}
												>
													<b>{comment.author?.fullName || 'Unknown'}:&nbsp;</b>
													<small>
														{comment.createdAt
															? DateTime.fromISO(comment.createdAt).toFormat('yyyy-MM-dd HH.mm')
															: 'Unknown date'}
													</small>
												</Box>
												<i>{comment.comment || ''}</i>
											</Box>
										))}
								</Box>
							</Box>
							<DialogContentText dangerouslySetInnerHTML={{ __html: text }} />
							{/* <TextField
								sx={{
									// mt: '10px',
									'& .MuiInputBase-root': {
										padding: '6px 8px',
										lineHeight: '1.2',
										// fontSize: '0.9rem',
									},
								}}
								autoFocus
								// label="Add New Comment"
								value={newComment}
								onChange={e => setNewComment(e.target.value)}
								type="text"
								fullWidth
								multiline
								rows={3}
							/> */}
						</Box>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							formik.resetForm();
							reset();
							setClose();
						}}
						sx={{
							textTransform: 'none',
						}}
					>
						Cancel
					</Button>
					<Button
						type="submit"
						// onClick={() => console.log('Button send clicked')}
						autoFocus
						sx={{
							textTransform: 'none',
						}}
					>
						Send
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};
